document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const qs = (selector, root = document) => root.querySelector(selector);
const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
const shopRoot = window.Shopify?.routes?.root || '/';

const revealElements = qsa('[data-reveal]');
if ('IntersectionObserver' in window) {
  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: .12 });
  revealElements.forEach((element) => revealObserver.observe(element));
} else {
  revealElements.forEach((element) => element.classList.add('is-visible'));
}

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
}[character]));

const formatMoney = (cents) => {
  const currency = window.Shopify?.currency?.active || 'EGP';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

const header = qs('[data-header]');
if (header) {
  window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 24), { passive: true });
  const toggle = qs('.menu-toggle', header);
  const nav = qs('.primary-nav', header);
  toggle?.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    nav?.classList.toggle('is-open', !open);
  });
}

qsa('[data-product-rail]').forEach((rail) => {
  const section = rail.closest('.shop-browser');
  const progress = qs('[data-rail-progress]', section);
  const update = () => {
    const available = rail.scrollWidth - rail.clientWidth;
    const ratio = available > 0 ? rail.scrollLeft / available : 0;
    if (progress) progress.style.transform = `scaleX(${Math.max(.08, ratio)})`;
  };
  qs('[data-rail-prev]', section)?.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * .72, behavior: 'smooth' }));
  qs('[data-rail-next]', section)?.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * .72, behavior: 'smooth' }));
  rail.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
});

const drawer = qs('[data-cart-drawer]');
const scrim = qs('[data-drawer-scrim]');
const cartItems = qs('[data-cart-items]');
const cartTotal = qs('[data-cart-total]');
const checkoutButton = qs('.cart-drawer .checkout-button');

const setDrawer = (open) => {
  if (!drawer || !scrim) return;
  drawer.classList.toggle('is-open', open);
  drawer.setAttribute('aria-hidden', String(!open));
  scrim.hidden = !open;
  document.body.classList.toggle('drawer-open', open);
  if (open) qs('[data-cart-close]', drawer)?.focus();
};

const renderCart = (cart) => {
  qsa('[data-cart-count]').forEach((node) => { node.textContent = String(cart.item_count).padStart(2, '0'); });
  if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);
  if (checkoutButton) checkoutButton.disabled = cart.item_count === 0;
  if (!cartItems) return;
  if (!cart.items.length) {
    cartItems.innerHTML = '<p class="cart-empty utility">YOUR BAG IS EMPTY.</p>';
    return;
  }
  cartItems.innerHTML = cart.items.map((item) => `
    <article class="cart-line" data-line-key="${escapeHtml(item.key)}">
      <a href="${escapeHtml(item.url)}" class="cart-line__image">${item.image ? `<img src="${escapeHtml(item.image)}&width=240" width="120" alt="${escapeHtml(item.product_title)}">` : ''}</a>
      <div class="cart-line__info">
        <a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>
        <span class="utility">${escapeHtml(item.variant_title || '')}</span>
        <span>${formatMoney(item.final_line_price)}</span>
        <div class="quantity"><button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}">−</button><span>${item.quantity}</span><button type="button" data-cart-quantity="${item.quantity + 1}">+</button></div>
      </div>
    </article>`).join('');
};

const getCart = async () => {
  const response = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load bag');
  const cart = await response.json();
  renderCart(cart);
  return cart;
};

qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
  setDrawer(true);
  try { await getCart(); } catch (error) { console.error(error); }
}));
qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setDrawer(false)));
scrim?.addEventListener('click', () => setDrawer(false));
document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setDrawer(false); });

cartItems?.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cart-quantity]');
  const line = event.target.closest('[data-line-key]');
  if (!button || !line) return;
  button.disabled = true;
  try {
    const response = await fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.dataset.lineKey, quantity: Number(button.dataset.cartQuantity) }),
    });
    if (!response.ok) throw new Error('Unable to update bag');
    renderCart(await response.json());
  } catch (error) {
    button.disabled = false;
    console.error(error);
  }
});

qsa('[data-product-form]').forEach((form) => {
  const status = qs('[data-product-form-status]', form);
  const submit = qs('[data-add-to-cart]', form);
  const variant = qs('[data-variant-select]', form);
  const variantButtons = qsa('[data-variant-option]', form);
  const price = qs('[data-product-price]', form.closest('[data-product-root]'));
  variant?.addEventListener('change', () => {
    const option = variant.selectedOptions[0];
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    variantButtons.forEach((button) => {
      const selected = button.dataset.variantId === variant.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  });
  variantButtons.forEach((button) => button.addEventListener('click', () => {
    if (!variant) return;
    variant.value = button.dataset.variantId;
    variant.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    if (status) status.textContent = 'ADDING / STAND BY';
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error((await response.json()).description || 'Unable to add item');
      await getCart();
      setDrawer(true);
      if (status) status.textContent = 'ADDED / BAG UPDATED';
    } catch (error) {
      if (status) status.textContent = error.message.toUpperCase();
    } finally {
      submit.disabled = false;
    }
  });
});
